import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { ProviderListSortCol, TProviderList, TProviderListResponse, TProviderListQuery, TProviderListSortCol, adaptProviderFromListResponse, adaptProviderListQueryToRequest } from '../../types';
import { BACKEND_ADMIN_PROVIDER } from '../../backend-paths';


export const providerListGetApi = createAsyncThunk(
	BACKEND_ADMIN_PROVIDER,
	(payload: TProviderListQuery | undefined, thunkApi) => {
		const params = adaptProviderListQueryToRequest(payload);

		return HttpService.get<TProviderListResponse>(BACKEND_ADMIN_PROVIDER, { params }).then(result => {
			return adaptProviderFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TProviderList;
	query: TProviderListQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: ProviderListSortCol.CREATED_AT,
	},
	error: undefined,
};

export const providerListSlice = createSlice({
	name: 'providerListSlice',
	initialState,
	reducers: {
		setProviderListQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setProviderListQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setProviderListQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setProviderListQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setProviderListQuerySortCol(state, action: PayloadAction<TProviderListSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearProviderList(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[providerListGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[providerListGetApi.fulfilled.type]: (state, action: PayloadAction<TProviderList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[providerListGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useProviderListSelector = () => useAppSelector(state => state[providerListSlice.name]);

export const { clearProviderList, setProviderListQueryLimit, setProviderListQueryOffset, setProviderListQuerySort, setProviderListQuerySortCol, setProviderListQuerySearch } = providerListSlice.actions;
